/**
 * React Hooks for Customer Operations
 *
 * MULTI-TENANCY: All queries filter by tenantId for data isolation
 */

import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  onSnapshot,
  QuerySnapshot,
  DocumentData,
} from 'firebase/firestore';
import { db } from '../firebase';
import { Customer } from '@/types';
import * as customerService from '../services/customer.service';
import { useTenantId, tenantIdReadUnion } from './useTenantId';
import { createdAtMs } from '@/utils/sortHelpers';

/**
 * Hook to get all customers with real-time updates
 */
export function useCustomers() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const tenantId = useTenantId();

  useEffect(() => {
    // Phase 0.5 Session A.5: tenant filter required for strict isolation rules.
    // NOTE: intentionally NO `orderBy('createdAt')` here — a Firestore orderBy
    // silently drops every doc missing that field, and legacy Pipedrive imports
    // predate `createdAt`. Ordering by it made those customers invisible to
    // global search. We sort client-side instead so field-absent docs survive.
    const q = query(
      collection(db, 'customers'),
      where('tenantId', 'in', tenantIdReadUnion(tenantId))
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot: QuerySnapshot<DocumentData>) => {
        const customersData = snapshot.docs.map(
          (doc) =>
            ({
              id: doc.id,
              ...doc.data(),
            }) as Customer
        );
        // Newest-first, tolerant of missing/Timestamp createdAt (sorts to end).
        customersData.sort((a, b) => createdAtMs(b.createdAt) - createdAtMs(a.createdAt));
        setCustomers(customersData);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching customers:', err);
        setError(err as Error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [tenantId]);

  return { customers, loading, error };
}

/**
 * Hook to get a single customer by ID with real-time updates
 */
export function useCustomer(customerId: string | null) {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!customerId) {
      setCustomer(null);
      setLoading(false);
      return;
    }

    // Fetch customer once (not real-time for single customer to save reads)
    customerService
      .getCustomer(customerId)
      .then((data) => {
        setCustomer(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching customer:', err);
        setError(err);
        setLoading(false);
      });
  }, [customerId]);

  return { customer, loading, error };
}

/**
 * Hook to create, update, and delete customers
 */
export function useCustomerMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createCustomer = async (
    data: Omit<Customer, 'id' | 'createdAt' | 'updatedAt'>,
    userId?: string
  ) => {
    setLoading(true);
    setError(null);
    try {
      const id = await customerService.createCustomer(data, userId);
      setLoading(false);
      return id;
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  const updateCustomer = async (
    customerId: string,
    data: Partial<Omit<Customer, 'id' | 'createdAt' | 'createdBy'>>
  ) => {
    setLoading(true);
    setError(null);
    try {
      await customerService.updateCustomer(customerId, data);
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  const deleteCustomer = async (customerId: string) => {
    setLoading(true);
    setError(null);
    try {
      await customerService.deleteCustomer(customerId);
      setLoading(false);
    } catch (err) {
      setError(err as Error);
      setLoading(false);
      throw err;
    }
  };

  return {
    createCustomer,
    updateCustomer,
    deleteCustomer,
    loading,
    error,
  };
}
